import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Home as HomeIcon } from "lucide-react";
import { useSettings } from "@/contexts/SettingsContext";

/**
 * /consultation — Full-bleed AI chat assistant.
 *
 * v3 (2026-02-17): Adds a floating "Home" button overlay in the top-left
 * corner, positioned at roughly the same vertical level as the chat
 * app's own "Restart Conversation" button (which sits in the top-right).
 * Together they form a balanced top bar: ← Home | ... | Restart →
 *
 * The button floats above the iframe via absolute positioning. The
 * iframe URL stays hidden from the address bar; visitors only ever see
 * builddirectafrica.com/consultation.
 */
export default function ConsultationPage() {
  const { settings } = useSettings();
  const consultationUrl =
    settings?.consultation_url || "https://guided-build-ai.emergent.host/";
  const brandName = settings?.brand_name || "BuildDirect Africa";

  useEffect(() => {
    document.title = `Consultation – ${brandName}`;
  }, [brandName]);

  return (
    <div
      className="fixed inset-0 bg-white"
      data-testid="consultation-page"
    >
      {consultationUrl ? (
        <iframe
          src={consultationUrl}
          title="BuildDirect Africa Consultation"
          data-testid="consultation-iframe"
          className="absolute inset-0 w-full h-full border-0 bg-white"
          allow="clipboard-write; microphone; camera"
          referrerPolicy="no-referrer-when-downgrade"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-slate-600 text-sm p-6 text-center">
          Consultation chat is being set up. Please check back shortly.
        </div>
      )}

      {/*
        Floating "Home" pill overlay — bottom-RIGHT corner so it never
        obstructs the chat app's "Continue on WhatsApp" pill that sits
        on the left at the bottom of the chat layout.
      */}
      <Link
        to="/"
        data-testid="consultation-home-button"
        aria-label="Back to home"
        title="Back to home"
        className="absolute bottom-3.5 right-3.5 z-50 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/95 hover:bg-white shadow-lg border border-slate-200 text-slate-700 hover:text-slate-900 text-xs font-medium transition-colors"
        style={{ marginBottom: "env(safe-area-inset-bottom, 0px)" }}
      >
        <HomeIcon className="w-3.5 h-3.5" strokeWidth={1.8} />
        <span>Home</span>
      </Link>
    </div>
  );
}
